// skipcq: JS-W1028, JS-0128
import React, { useEffect, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {
  METRIC_APP_MAPPING_OPTIONS,
  validateMetricKey,
} from '../../domain/metricConstants';
import {
  METRIC_IMAGE_MAX_BYTES,
  METRIC_IMAGE_SIZE_PX,
} from './metricImageUpload';

const METRIC_IMAGE_HINT = `Metric images must be PNG, ${METRIC_IMAGE_SIZE_PX}×${METRIC_IMAGE_SIZE_PX} pixels, under ${METRIC_IMAGE_MAX_BYTES / 1024}KB. Upload them on the Metric images tab.`;

function EditMetricDialogFields({
  isEdit,
  form,
  onChange,
  availableImages,
  imagesLoading,
  selectValue,
}) {
  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField
        label="Key"
        name="key"
        value={form.key}
        onChange={onChange}
        fullWidth
        required
        disabled={isEdit}
        helperText={
          isEdit
            ? 'Key cannot be changed after creation.'
            : 'camelCase only, e.g. abvRange, leadingBeerStyle.'
        }
      />
      <TextField
        label="Name"
        name="name"
        value={form.name}
        onChange={onChange}
        required
        fullWidth
      />
      <TextField
        label="Help text"
        name="helpText"
        value={form.helpText}
        onChange={onChange}
        fullWidth
        multiline
        minRows={3}
      />
      <FormControl fullWidth>
        <InputLabel id="metric-app-mapping-label">App mapping</InputLabel>
        <Select
          labelId="metric-app-mapping-label"
          label="App mapping"
          name="appMapping"
          value={form.appMapping ?? ''}
          onChange={onChange}
        >
          {METRIC_APP_MAPPING_OPTIONS.map((opt) => (
            <MenuItem key={opt.value || 'none'} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Sort order"
        name="sortOrder"
        type="number"
        value={
          typeof form.sortOrder === 'number' && !Number.isNaN(form.sortOrder)
            ? form.sortOrder
            : Number(form.sortOrder) || 0
        }
        onChange={onChange}
        fullWidth
        inputProps={{ step: 1 }}
      />
      <FormControl fullWidth required>
        <InputLabel id="metric-image-label">Image</InputLabel>
        <Select
          labelId="metric-image-label"
          label="Image"
          name="image"
          value={selectValue}
          onChange={onChange}
          disabled={!availableImages.length && !imagesLoading}
        >
          {availableImages.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {imagesLoading && (
        <Alert severity="info">Loading image list from Storage…</Alert>
      )}
    </Stack>
  );
}

/**
 * Create or edit a metric definition.
 */
function EditMetricDialog({
  open,
  mode,
  submitting,
  errorMsg,
  form,
  availableImages = [],
  imagesLoading = false,
  onClose,
  onChange,
  onSubmit,
}) {
  const isEdit = mode === 'edit';
  const trimmedImage = (form.image ?? '').trim();
  const imageOk =
    Boolean(trimmedImage) && availableImages.includes(trimmedImage);
  const selectValue = imageOk
    ? trimmedImage
    : (availableImages[0] ?? '');

  const keyOk = useMemo(() => {
    if (isEdit) return true;
    return validateMetricKey(form.key).ok;
  }, [form.key, isEdit]);

  const canSubmit = useMemo(() => {
    const nameOk = Boolean(form.name?.trim());
    return keyOk && nameOk && imageOk && !submitting;
  }, [form.name, imageOk, keyOk, submitting]);

  useEffect(() => {
    if (!open) return;
    const trimmed = (form.image ?? '').trim();
    const resolvedOk =
      Boolean(trimmed) && availableImages.includes(trimmed);
    const resolvedImage = resolvedOk
      ? trimmed
      : (availableImages[0] ?? '');
    if (resolvedImage !== (form.image ?? '')) {
      onChange({
        target: { name: 'image', value: resolvedImage },
      });
    }
  }, [open, availableImages, form.image, onChange]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit metric' : 'Add metric'}</DialogTitle>
      <DialogContent>
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          {METRIC_IMAGE_HINT}
        </Alert>
        <EditMetricDialogFields
          isEdit={isEdit}
          form={form}
          onChange={onChange}
          availableImages={availableImages}
          imagesLoading={imagesLoading}
          selectValue={selectValue}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!canSubmit}
          variant="contained"
        >
          {submitting
            ? isEdit
              ? 'Saving…'
              : 'Creating…'
            : isEdit
              ? 'Save'
              : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditMetricDialog;
